// React
import { useEffect, useMemo, useState } from 'react';

// Material UI
import { Box, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';

// Custom components
import BankingWidgetSummary from './banking-widget-summary';
import ChartColumnStacked from 'src/sections/charts/chart-column-stacked';
import ChartColumnStackedPercent from 'src/sections/charts/chart-collumn-stacked-percent';
import ChartMixed from 'src/sections/charts/chart-mixed';
import Iconify from 'src/components/iconify';
import { SplashScreen } from 'src/components/loading-screen';
import { usePreviousLocation } from 'src/components/previousLocationContext';

// Context and Redux
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterKeyword, setFilterLoadedKeyword, setFilterLoadedPlatform, setFilterMinDatePicker, setFilterTimestep } from 'src/context/reducers/filter-settings/filterSlice';
import { useGetKpisQuery } from 'src/context/api/trafficAnalysis/api';

// Utilities and others
import { fDate, getDateRangeIso, getLast12WeeksDateRange } from 'src/utils/format-time';
import { useLocales } from 'src/locales';
import ConversionsAnalysis from './conversionsAnalysis';
import { subDays } from 'date-fns';
import { useLocation } from 'react-router';

interface TimeSeriesData {
    adClicks: number;
    adCost: number;
    blendedCtr: number | null;
    brandTraffic: number;
    date_interval: string;
    organicClicks: number;
    organicSearches: number;
    year_week?: string;
    year_month?: string;
    banking_date?: string;
    [key: string]: any; // To allow dynamic property access
}

// Constants
const ColorSchema = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    TERTIARY: 'tertiary',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success'
} as const;

const TRAFFIC_BRANDED_FORMAT = [
    {
        value: "Value",
        label: "dashboard.global.value",
    },
    {
        value: "Percent",
        label: "dashboard.global.percent",
    },
];

const formatLabels = (data: TimeSeriesData[], timestep: string, lang: string): string[] => {
    const labels = data.map(item => {
        let dateStr = '';
        switch (timestep) {
            case 'year_week':
                dateStr = item.year_week || item.date_interval || '';
                break;
            case 'year_month':
                dateStr = item.year_month || item.date_interval || '';
                break;
            default:
                dateStr = item.date_interval || '';
        }
        return fDate(dateStr, 'yyyy-MM-dd');
    });
    return labels;
};

// Chart components
const renderTrafficCostChart = (
    isFetchingKpis: boolean,
    kpisData: any,
    kpisError: any,
    BrandedTrafficPerformanceChart: any[],
    customLabels: string[],
    t: (key: string) => string,
    theme: any,
) => (
    <Paper
        sx={{
            backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
            boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
            height: '500px',
            borderRadius: 2,
            marginTop: 2,
            marginBottom: 1
        }}
    >
        {isFetchingKpis || (!kpisData && !kpisError)
            ? <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                <>
                    <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                    <Typography color={"success.main"}>Active</Typography>
                </>
            </Skeleton>
            : <div>
                <div style={{ paddingTop: 18, paddingLeft: 25, paddingRight: 25 }}>
                    <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>{t('dashboard.brandTraffic.trafficCost')}</Typography>
                    <Typography variant="h6" sx={{ mb: 5 }}>
                        {t('dashboard.brandTraffic.blendedCtrAndAdCost')}
                    </Typography>
                </div>
                <ChartMixed
                    series={kpisData ? BrandedTrafficPerformanceChart : []}
                    labels={customLabels}
                    height={350}
                />
            </div>
        }
    </Paper>
);

// Chart components
const renderTrafficDistributionChart = (
    isFetchingKpis: boolean,
    kpisData: any,
    kpisError: any,
    BlendedCTRAdCostChart: any[],
    customLabels: string[],
    selectedDisplayGraph: string,
    setSelectedDisplayGraphValue: (value: string) => void,
    t: (key: string) => string,
    theme: any,
) => (
    <Paper
        sx={{
            backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
            boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
            height: '500px',
            borderRadius: 2,
            marginTop: 2,
            marginBottom: 1
        }}
    >
        {isFetchingKpis || (!kpisData && !kpisError)
            ? <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                <Typography color={"success.main"}>Active</Typography>
            </Skeleton>
            : <div style={{ padding: 20, paddingLeft: 25, paddingRight: 25 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ marginBottom: 20 }}>
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>{t('dashboard.brandTraffic.organicTrafficVsAdTraffic')}</Typography>
                        <Typography variant="h6" sx={{ mb: 5 }}>{t('dashboard.brandTraffic.branded_traffic_distribution')}</Typography>
                    </div>
                    <TextField
                        sx={{ width: 150, height: 50, position: 'relative', top: -25 }}
                        size="small"
                        variant="outlined"
                        select
                        fullWidth
                        label={t('dashboard.global.display')}
                        value={selectedDisplayGraph}
                        onChange={(event) => setSelectedDisplayGraphValue(event.target.value)}
                    >
                        {TRAFFIC_BRANDED_FORMAT.map((option, index) => (
                            <MenuItem key={index} value={option.value}>
                                {t(option.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {selectedDisplayGraph === "Value"
                    ? <ChartColumnStacked
                        series={kpisData ? BlendedCTRAdCostChart : []}
                        labels={customLabels}
                        height={350}
                    />
                    : <ChartColumnStackedPercent
                        series={kpisData ? BlendedCTRAdCostChart : []}
                        labels={customLabels}
                    />
                }
            </div>
        }
    </Paper>
);

// Main component
export default function TrafficAnalysis() {
    const { t, currentLang } = useLocales();
    const theme = useTheme();
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);
    const [selectedDisplayGraph, setSelectedDisplayGraphValue] = useState('Value');
    const workspace: any = useCurrentWorkspace();
    const prevLocation = usePreviousLocation();
    const location = useLocation();

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: kpisData, error: kpisError, isFetching: isFetchingKpis } = useGetKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compare ? (filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null) : null,
        compareEndDate: filterStore.compare ? (filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null) : null,
        workspaceId: workspace?.id,
        countries: filterStore.countries,
        devices: filterStore.devices,
        keywords: filterStore.keywords,
        platforms: filterStore.platforms,
        timestep: filterStore.timestep,
        tags: filterStore.tags.map((tag: any) => tag.label),
    }, {
        skip: skipQuery,
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (kpisData?.min_date) {
            dispatch(setFilterMinDatePicker(kpisData?.min_date));
        }
    }, [kpisData?.min_date, dispatch]);

    const getRevenueOrConversionsTexts = (workspace: any) => {
        const isLead = workspace?.template === "Lead";
        const isEcommerce = workspace?.template === "E-commerce";

        const hasGA4 =
            Array.isArray(workspace?.conversions_google_analytics) &&
            workspace.conversions_google_analytics.length > 0;

        const hasAds =
            Array.isArray(workspace?.conversions_google_ads) &&
            workspace.conversions_google_ads.length > 0;

        const isCustom = workspace?.custom === true;

        if (isLead) {
            if (isCustom) {
                return {
                    label: t("dashboard.global.custom_based_conversions"),
                    definition: t("definitions.custom_based_conversions"),
                };
            }
            if (hasGA4) {
                return {
                    label: t("dashboard.global.total_search_conversions"),
                    definition: t("definitions.total_search_conversions"),
                };
            }
            if (hasAds) {
                return {
                    label: t("dashboard.global.brand_conversions_gads"),
                    definition: t("definitions.brand_conversions_gads"),
                };
            }
        }

        if (isEcommerce) {
            if (isCustom) {
                return {
                    label: t("dashboard.global.custom_based_revenue"),
                    definition: t("definitions.custom_based_revenue"),
                };
            }
            if (hasGA4) {
                return {
                    label: t("dashboard.global.total_search_revenue"),
                    definition: t("definitions.total_search_revenue"),
                    isCurrency: true
                };
            }
            if (hasAds) {
                return {
                    label: t("dashboard.global.brand_revenue_gads"),
                    definition: t("definitions.brand_revenue_gads"),
                };
            }
        }

        return { label: "N/A", definition: "N/A" };
    };

    const revenueOrConversionsTexts = useMemo(
        () => getRevenueOrConversionsTexts(workspace),
        [workspace, t]
    );

    const bankinStats = useMemo(() => [
        {
            title: t('dashboard.brandTraffic.brandSearches'),
            value: kpisData?.organicSearches ?? 0,
            compare: kpisData?.organicSearchesCompare,
            isPercent: false,
            color: ColorSchema.ERROR,
            invertedColor: false,
            isCurrency: false,
            definition: t("definitions.brand_searches"),
        },
        {
            title: t('dashboard.brandTraffic.totalBrandTraffic'),
            value: kpisData?.brandTraffic ?? 0,
            compare: kpisData?.brandTrafficCompare,
            color: ColorSchema.SECONDARY,
            definition: t("definitions.brand_traffic"),
            isCurrency: false,
            isPercent: false,
        },
        {
            title: t('dashboard.brandTraffic.blendedCtr'),
            value: (kpisData?.blendedCtr ?? 0),
            compare: kpisData?.blendedCtrCompare,
            color: ColorSchema.INFO,
            isPercent: true,
            isCurrency: false,
            definition: t("definitions.blended_ctr"),

        },
        {
            title: revenueOrConversionsTexts.label,
            value: kpisData?.revenue_or_conversions ?? 0,
            compare: kpisData?.revenue_or_conversions_compare,
            color: ColorSchema.TERTIARY,
            isPercent: false,
            isCurrency: revenueOrConversionsTexts?.isCurrency,
            definition: revenueOrConversionsTexts.definition,
        },
        {
            title: t('dashboard.global.ad_cost'),
            value: kpisData?.adCost ?? 0,
            compare: kpisData?.adCostCompare,
            isCurrency: true,
            invertedColor: true,
            color: ColorSchema.WARNING,
            definition: t("definitions.ad_cost"),
        }
    ], [kpisData, theme.palette.mode, t, filterStore.timestep, currentLang.value, filterStore.compare, filterStore?.savingsFormulaFilter]);

    const [BrandedTrafficPerformanceChart, BrandedTrafficDistributionChart] = useMemo(() => {
        const performanceChart = [];
        const distributionChart = [];

        if (workspace?.template !== "E-commerce" && !workspace?.shoppingIncluded) {
            // --- Branded Traffic Performance (gauche) ---
            performanceChart.push(
                {
                    name: t('dashboard.global.blended_ctr'),
                    type: 'line',
                    data: kpisData?.time_series_data?.map((item: any) => (item?.blendedCtr ?? 0)) ?? [],
                    yAxisIndex: 1,
                    color: "#5119b7"
                },
                {
                    name: t('dashboard.brandTraffic.adCost'),
                    type: 'bar',
                    data: kpisData?.time_series_data?.map((item: any) => item?.adCost ?? 0) ?? [],
                    yAxisIndex: 0,
                    color: "#ffab00"
                },
                {
                    name: t('dashboard.global.blended_cpc'),
                    type: 'line',
                    data: kpisData?.time_series_data?.map((item: any) => item?.blendedCpc ?? 0) ?? [],
                    yAxisIndex: 1,
                    color: theme.palette.mode === "dark" ? theme.palette.cpc.darker : theme.palette.cpc.lighter
                }
            );

            // --- Branded Traffic Distribution (droite) ---
            distributionChart.push(
                {
                    name: t('dashboard.brandTraffic.organicBrandTraffic'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.organicClicks ?? 0) ?? []
                },
                {
                    name: t('dashboard.brandTraffic.searchPaid'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.adClicks ?? 0) ?? []
                },
                {
                    name: t('dashboard.brandTraffic.uncapturedBrandTraffic'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.uncapturedBrandTraffic ?? 0) ?? []
                }
            );
        } else {
            // --- Branded Traffic Performance (gauche) ---
            performanceChart.push(
                {
                    name: t('dashboard.global.blended_ctr'),
                    type: 'line',
                    data: kpisData?.time_series_data?.map((item: any) => (item?.blendedCtr ?? 0)) ?? [],
                    yAxisIndex: 1,
                    color: theme.palette.metrics.blendedCtr.main
                },
                {
                    name: t('dashboard.brandTraffic.searchAdCost'),
                    type: 'bar',
                    data: kpisData?.time_series_data?.map((item: any) => item?.adCostSearch ?? 0) ?? [],
                    yAxisIndex: 0,
                    group: 'adCost'
                },
                {
                    name: t('dashboard.brandTraffic.shoppingAdCost'),
                    type: 'bar',
                    data: kpisData?.time_series_data?.map((item: any) => item?.adCostShopping ?? 0) ?? [],
                    yAxisIndex: 0,
                    color: theme.palette.metrics.shoppingAdCost.main,
                    group: 'adCost'
                }
            );

            // Ajouter PMax seulement si toutes les données ont cette propriété
            const hasPmaxData = kpisData?.time_series_data?.every((item: any) => item?.adCostPmax !== undefined);

            if (hasPmaxData && workspace.shoppingIncluded === false) {
                performanceChart.push(
                    {
                        name: t('dashboard.brandTraffic.pmaxAdCost'),
                        type: 'bar',
                        data: kpisData?.time_series_data?.map((item: any) => item?.adCostPmax ?? 0) ?? [],
                        yAxisIndex: 0,
                        color: theme.palette.metrics.pmaxAdCost.main,
                        group: 'adCost'
                    }
                );
            }

            performanceChart.push(
                {
                    name: t('dashboard.global.blended_cpc'),
                    type: 'line',
                    data: kpisData?.time_series_data?.map((item: any) => item?.blendedCpc ?? 0) ?? [],
                    yAxisIndex: 1,
                    color: theme.palette.mode === "dark" ? theme.palette.cpc.darker : theme.palette.cpc.lighter
                }
            );

            // --- Branded Traffic Distribution (droite) ---
            distributionChart.push(
                {
                    name: t('dashboard.brandTraffic.organicBrandTraffic'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.organicClicks ?? 0) ?? []
                },
                {
                    name: t('dashboard.brandTraffic.searchPaid'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.adClicksSearch ?? 0) ?? []
                },
                {
                    name: t('dashboard.brandTraffic.shoppingPaid'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.adClicksShopping ?? 0) ?? []
                }
            );

            // Ajouter PMax seulement si toutes les données ont cette propriété
            const hasPmaxClicksData = kpisData?.time_series_data?.every((item: any) => item?.adClicksPmax !== undefined);

            if (hasPmaxClicksData && workspace.shoppingIncluded === false) {
                distributionChart.push(
                    {
                        name: t('dashboard.brandTraffic.pmaxPaid'),
                        data: kpisData?.time_series_data?.map((item: any) => item?.adClicksPmax ?? 0) ?? []
                    }
                );
            }

            distributionChart.push(
                {
                    name: t('dashboard.brandTraffic.uncapturedBrandTraffic'),
                    data: kpisData?.time_series_data?.map((item: any) => item?.uncapturedBrandTraffic ?? 0) ?? []
                }
            );
        }

        return [performanceChart, distributionChart];
    }, [kpisData, workspace?.template, t, theme.palette.metrics]);

    useEffect(() => {
        if (filterStore?.searchPerfDateOverridden) return; // keep the persisted override
        const { start, end } = getLast12WeeksDateRange();
        dispatch(setFilterDate({ start, end }));
        dispatch(setFilterTimestep("year_week"));
        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [])

    useEffect(() => {
        if (workspace?.name.includes('Demo')) {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
            const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024

            const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

            dispatch(setFilterDate({ start, end }));
            dispatch(setFilterTimestep("date_day"))
        }
    }, [workspace, location?.pathname])

    return (
        <>
            {(!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices)
                ? <SplashScreen />
                : <Box px={"24px"}>
                    <Grid container spacing={2}>
                        {bankinStats.map((stat, index) => (
                            <Grid key={index} xs={6} md={4} lg={2.4}>
                                <BankingWidgetSummary
                                    invertedColor={stat?.invertedColor}
                                    definition={stat?.definition}
                                    title={stat?.title}
                                    color={stat?.color}
                                    isFetching={isFetchingKpis || (!kpisData && !kpisError)}
                                    compare={stat?.compare}
                                    isPercent={stat?.isPercent}
                                    total={stat?.value}
                                    isCurrency={stat?.isCurrency}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <div>
                        <Grid container spacing={2}>
                            <Grid xs={12} md={6}>
                                {renderTrafficDistributionChart(
                                    isFetchingKpis,
                                    kpisData,
                                    kpisError,
                                    BrandedTrafficDistributionChart,
                                    formatLabels(kpisData?.time_series_data ?? [], filterStore.timestep, currentLang.value),
                                    selectedDisplayGraph,
                                    setSelectedDisplayGraphValue,
                                    t,
                                    theme
                                )}
                            </Grid>
                            <Grid xs={12} md={6}>
                                {renderTrafficCostChart(
                                    isFetchingKpis,
                                    kpisData,
                                    kpisError,
                                    BrandedTrafficPerformanceChart,
                                    formatLabels(kpisData?.time_series_data ?? [], filterStore.timestep, currentLang.value),
                                    t,
                                    theme
                                )}
                            </Grid>
                        </Grid>
                    </div>


                    <ConversionsAnalysis />

                </Box>
            }
        </>
    )
}